import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User as FirebaseAuthUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@config/firebase';
import { useAppDispatch } from '@store/hooks';
import { setUser, setUserProfile, setLoading } from '@store/slices/authSlice';
import type { User } from '@/types';

export const useAuth = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const handleUserChange = async (firebaseUser: FirebaseAuthUser | null) => {
      dispatch(setUser(firebaseUser ?? null));

      try {
        if (firebaseUser) {
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            if (!userDoc.exists()) {
              dispatch(setLoading(false));
              return;
            }

            const userData = userDoc.data() as Record<string, unknown>;

            const serializableUserData: Partial<User> = {
              ...(userData as Partial<User>),
              emailVerified: firebaseUser.emailVerified,
              photoURL:
                (userData.photoURL as string | undefined) || firebaseUser.photoURL || undefined,
              createdAt:
                (userData.createdAt as { toDate?: () => Date } | undefined)
                  ?.toDate?.()
                  ?.toISOString() || (userData.createdAt as string),
              updatedAt:
                (userData.updatedAt as { toDate?: () => Date } | undefined)
                  ?.toDate?.()
                  ?.toISOString() || (userData.updatedAt as string),
              lastLoginAt:
                (userData.lastLoginAt as { toDate?: () => Date } | undefined)
                  ?.toDate?.()
                  ?.toISOString() || (userData.lastLoginAt as string | undefined),
              nextBillingDate:
                (userData.nextBillingDate as { toDate?: () => Date } | undefined)
                  ?.toDate?.()
                  ?.toISOString() || (userData.nextBillingDate as string | undefined),
            };

            dispatch(setUserProfile(serializableUserData as unknown as User));
          } catch (err) {
            console.error('[useAuth] Error fetching user profile:', err);
          }
        } else {
          dispatch(setUserProfile(null));
        }
      } finally {
        dispatch(setLoading(false));
      }
    };

    const unsubscribe = onAuthStateChanged(auth, handleUserChange);
    return () => unsubscribe();
  }, [dispatch]);
};
